import { type ReactNode, useEffect, useState } from "react";
import { SectionBoundary } from "./SectionBoundary";
import { useFlag, type StabilityFlag } from "@/lib/stability/featureFlags";

/**
 * useHydrated — returns false on SSR and first client render, true after
 * mount. Lets us defer client-only / localStorage-driven UI until React
 * has fully hydrated, eliminating hydration mismatches without touching
 * each individual subsystem.
 */
function useHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { setHydrated(true); }, []);
  return hydrated;
}

interface Props {
  name: string;
  flag: StabilityFlag;
  children: ReactNode;
  /** Optional silent fallback (default: nothing rendered on error or flag-off). */
  fallback?: ReactNode;
}

/**
 * IsolatedFlag — combines runtime kill-switch (useFlag) with a SectionBoundary
 * so a volatile subsystem can fail or be disabled without collapsing the host
 * route. When the flag is OFF, children never mount (no effects, no listeners,
 * no polling). When children throw, the elegant lightweight fallback renders.
 * Children are also deferred until after hydration to avoid SSR/client
 * mismatches in subsystems that read localStorage or window state.
 */
export function IsolatedFlag({ name, flag, children, fallback = null }: Props) {
  const enabled = useFlag(flag);
  const hydrated = useHydrated();
  if (!enabled || !hydrated) return <>{fallback}</>;
  return (
    <SectionBoundary name={name} fallback={fallback}>
      {children}
    </SectionBoundary>
  );
}

interface IsolatedProps {
  name: string;
  children: ReactNode;
  fallback?: ReactNode;
}

/** Pure isolation (no flag gate) — convenience wrapper for SectionBoundary. */
export function Isolated({ name, children, fallback = null }: IsolatedProps) {
  const hydrated = useHydrated();
  if (!hydrated) return <>{fallback}</>;
  return (
    <SectionBoundary name={name} fallback={fallback}>
      {children}
    </SectionBoundary>
  );
}